import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { CustomerdatabaseService } from './customerdatabase.service';
import { AuthService } from './auth.service';

export interface ExternalJobForMatching {
  id: string;
  jobTitle: string;
  title?: string;
  description?: string;
  location?: string;
  department?: string;
  requirements?: string[];
  qualifications?: string;
  tasks?: string;
  employmentType?: string;
  type?: string;
  applicationUrl?: string;
  sourceUrl?: string;
  company?: string;
}

export interface JobMatchResult {
  candidate: ShortlistCandidate;
  job: ExternalJobForMatching;
  matchScore: number;
  matchReasons: string[];
}

export interface ShortlistCandidate {
  id: string;
  full_name: string;
  linkedinUrl?: string;
  email?: string;
  phone?: string;
  statusId: number;
  shortlistedBy: string;
  shortlistedByAvatar?: string;
  shortlistedOn: Date;
  job_title?: string;
  job_company_name?: string;
  job_company_logo?: string;
  isFavorite?: boolean;
  jobOfferId?: string;
  jobOfferTitle?: string;
  projectId?: string;
  projectName?: string;
  score?: number;
  reason?: string;
  headline?: string;
  location?: string;
  profilePictureUrl?: string;
  tags?: any[];
  applicant_id?: string;
  skills?: string;
  experience?: string;
  education?: string;
  languages?: string;
  availability?: string;
  notes?: string;
  isImportedApplicant?: boolean;
}

export interface ShortlistProject {
  id: string;
  name: string;
  description?: string;
  searchQuery?: string;
  arsSessionId?: string;
  createdBy: string;
  createdOn: Date;
  candidateCount: number;
}

export interface ShortlistStatus {
  id: number;
  label: string;
  color: string;
}

export const SHORTLIST_STATUSES: ShortlistStatus[] = [
  { id: 0, label: 'Not Contacted', color: '#74b9ff' },
  { id: 1, label: 'Contacted', color: '#ffeaa7' },
  { id: 2, label: 'Replied', color: '#55efc4' },
  { id: 3, label: 'Interview', color: '#a29bfe' },
  { id: 4, label: 'Hired', color: '#00b894' },
  { id: 5, label: 'Rejected', color: '#ff7675' }
];

@Injectable({
  providedIn: 'root'
})
export class ShortlistService {
  private candidates$ = new BehaviorSubject<ShortlistCandidate[]>([]);
  private projects$ = new BehaviorSubject<ShortlistProject[]>([]);
  private loading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private customerDB: CustomerdatabaseService,
    private authService: AuthService
  ) {}

  getCandidates(): Observable<ShortlistCandidate[]> {
    return this.candidates$.asObservable();
  }

  getCandidatesSnapshot(): ShortlistCandidate[] {
    return this.candidates$.value;
  }

  getLoading(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  private getCustomerFirestore(): firebase.firestore.Firestore | null {
    if (!this.customerDB.app2) return null;
    return this.customerDB.app2.firestore;
  }

  async loadCandidates(): Promise<ShortlistCandidate[]> {
    this.loading$.next(true);
    try {
      const db = this.getCustomerFirestore();
      if (!db) {
        this.loading$.next(false);
        return [];
      }

      const snap = await db
        .collection('shortlist')
        .orderBy('shortlistedOn', 'desc')
        .get();

      const candidates: ShortlistCandidate[] = [];
      snap.forEach(doc => {
        const data = doc.data();
        candidates.push({
          id: doc.id,
          full_name: data.full_name || '',
          linkedinUrl: data.linkedinUrl || '',
          email: data.email || '',
          phone: data.phone || '',
          statusId: data.statusId ?? 0,
          shortlistedBy: data.shortlistedBy || '',
          shortlistedByAvatar: data.shortlistedByAvatar || '',
          shortlistedOn: data.shortlistedOn?.toDate() || new Date(),
          job_title: data.job_title || '',
          job_company_name: data.job_company_name || '',
          job_company_logo: data.job_company_logo || '',
          isFavorite: data.isFavorite || false,
          jobOfferId: data.jobOfferId || '',
          jobOfferTitle: data.jobOfferTitle || '',
          projectId: data.projectId || '',
          projectName: data.projectName || '',
          score: data.score,
          reason: data.reason,
          headline: data.headline || '',
          location: data.location || '',
          profilePictureUrl: data.profilePictureUrl || '',
          tags: data.tags || [],
          applicant_id: data.applicant_id || ''
        });
      });

      this.candidates$.next(candidates);
      this.loading$.next(false);
      return candidates;
    } catch (error) {
      console.error('[Shortlist] loadCandidates error:', error);
      this.loading$.next(false);
      return [];
    }
  }

  async addCandidate(candidate: Partial<ShortlistCandidate>): Promise<string> {
    const db = this.getCustomerFirestore();
    if (!db) throw new Error('Customer database not connected');

    const userName = this.authService.returnUser() || '';
    const data: any = {
      full_name: candidate.full_name || '',
      linkedinUrl: candidate.linkedinUrl || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      statusId: candidate.statusId ?? 0,
      shortlistedBy: userName.split('@')[0] || 'Unknown',
      shortlistedByAvatar: candidate.shortlistedByAvatar || '',
      shortlistedOn: firebase.firestore.FieldValue.serverTimestamp(),
      job_title: candidate.job_title || '',
      job_company_name: candidate.job_company_name || '',
      job_company_logo: candidate.job_company_logo || '',
      isFavorite: candidate.isFavorite || false,
      jobOfferId: candidate.jobOfferId || '',
      jobOfferTitle: candidate.jobOfferTitle || ''
    };

    const docRef = await db.collection('shortlist').add(data);

    const newCandidate: ShortlistCandidate = {
      ...data,
      id: docRef.id,
      shortlistedOn: new Date()
    };
    const current = this.candidates$.value;
    this.candidates$.next([newCandidate, ...current]);

    return docRef.id;
  }

  async updateCandidateStatus(candidateId: string, statusId: number): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    await db.collection('shortlist').doc(candidateId).update({ statusId });

    const current = this.candidates$.value;
    const updated = current.map(c =>
      c.id === candidateId ? { ...c, statusId } : c
    );
    this.candidates$.next(updated);
  }

  async toggleFavorite(candidateId: string): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    const current = this.candidates$.value;
    const candidate = current.find(c => c.id === candidateId);
    if (!candidate) return;

    const newValue = !candidate.isFavorite;
    await db.collection('shortlist').doc(candidateId).update({ isFavorite: newValue });

    const updated = current.map(c =>
      c.id === candidateId ? { ...c, isFavorite: newValue } : c
    );
    this.candidates$.next(updated);
  }

  async removeCandidate(candidateId: string): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    await db.collection('shortlist').doc(candidateId).delete();

    const current = this.candidates$.value;
    this.candidates$.next(current.filter(c => c.id !== candidateId));
  }

  async removeCandidates(candidateIds: string[]): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    const batch = db.batch();
    candidateIds.forEach(id => {
      batch.delete(db.collection('shortlist').doc(id));
    });
    await batch.commit();

    const current = this.candidates$.value;
    this.candidates$.next(current.filter(c => !candidateIds.includes(c.id)));
  }

  async loadCandidatesByJobOffer(jobOfferId: string): Promise<ShortlistCandidate[]> {
    const all = this.candidates$.value;
    if (all.length === 0) {
      await this.loadCandidates();
    }
    return this.candidates$.value.filter(c => c.jobOfferId === jobOfferId);
  }

  getDistinctJobOffers(): { id: string; title: string }[] {
    const map = new Map<string, string>();
    this.candidates$.value.forEach(c => {
      if (c.jobOfferId) {
        map.set(c.jobOfferId, c.jobOfferTitle || c.jobOfferId);
      }
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }

  getStatusById(statusId: number): ShortlistStatus {
    return SHORTLIST_STATUSES.find(s => s.id === statusId) || SHORTLIST_STATUSES[0];
  }

  getStatuses(): ShortlistStatus[] {
    return SHORTLIST_STATUSES;
  }

  getProjects(): Observable<ShortlistProject[]> {
    return this.projects$.asObservable();
  }

  getProjectsSnapshot(): ShortlistProject[] {
    return this.projects$.value;
  }

  async loadProjects(): Promise<ShortlistProject[]> {
    try {
      const db = this.getCustomerFirestore();
      if (!db) return [];

      const snap = await db
        .collection('shortlist_projects')
        .orderBy('createdOn', 'desc')
        .get();

      const projects: ShortlistProject[] = [];
      snap.forEach(doc => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          searchQuery: data.searchQuery || '',
          arsSessionId: data.arsSessionId || '',
          createdBy: data.createdBy || '',
          createdOn: data.createdOn?.toDate() || new Date(),
          candidateCount: data.candidateCount || 0
        });
      });

      this.projects$.next(projects);
      return projects;
    } catch (error) {
      console.error('[Shortlist] loadProjects error:', error);
      return [];
    }
  }

  async createProject(project: Partial<ShortlistProject>): Promise<string> {
    const db = this.getCustomerFirestore();
    if (!db) throw new Error('Customer database not connected');

    const userName = this.authService.returnUser() || '';
    const data: any = {
      name: project.name || 'Neues Projekt',
      description: project.description || '',
      searchQuery: project.searchQuery || '',
      arsSessionId: project.arsSessionId || '',
      createdBy: userName.split('@')[0] || 'Unknown',
      createdOn: firebase.firestore.FieldValue.serverTimestamp(),
      candidateCount: 0
    };

    const docRef = await db.collection('shortlist_projects').add(data);

    const newProject: ShortlistProject = {
      ...data,
      id: docRef.id,
      createdOn: new Date()
    };
    const current = this.projects$.value;
    this.projects$.next([newProject, ...current]);

    return docRef.id;
  }

  async getOrCreateProjectForSession(arsSessionId: string, searchQuery: string): Promise<string> {
    const db = this.getCustomerFirestore();
    if (!db) throw new Error('Customer database not connected');

    const existing = await db
      .collection('shortlist_projects')
      .where('arsSessionId', '==', arsSessionId)
      .limit(1)
      .get();

    if (!existing.empty) {
      console.log('[Shortlist][project] reusing existing project', existing.docs[0].id, 'for session', arsSessionId);
      return existing.docs[0].id;
    }

    const projectName = this.generateProjectName(searchQuery);
    const newId = await this.createProject({
      name: projectName,
      searchQuery,
      arsSessionId
    });
    console.log('[Shortlist][project] created new project', newId, 'for session', arsSessionId);
    return newId;
  }

  private generateProjectName(searchQuery: string): string {
    if (!searchQuery) return `Projekt ${new Date().toLocaleDateString('de-DE')}`;
    const truncated = searchQuery.length > 50 ? searchQuery.substring(0, 50) + '...' : searchQuery;
    return truncated;
  }

  isAlreadyShortlisted(applicantId: string): boolean {
    if (!applicantId) return false;
    return this.candidates$.value.some(c => c.applicant_id === applicantId);
  }

  async isAlreadyShortlistedInDb(applicantId: string): Promise<boolean> {
    if (!applicantId) return false;
    const db = this.getCustomerFirestore();
    if (!db) return false;
    
    try {
      const snap = await db.collection('shortlist')
        .where('applicant_id', '==', applicantId)
        .limit(1)
        .get();
      return !snap.empty;
    } catch {
      return false;
    }
  }

  isAlreadyShortlistedInProject(applicantId: string, projectId: string): boolean {
    if (!applicantId || !projectId) return false;
    return this.candidates$.value.some(c => c.applicant_id === applicantId && c.projectId === projectId);
  }

  async isAlreadyShortlistedInProjectDb(applicantId: string, projectId: string): Promise<boolean> {
    if (!applicantId || !projectId) return false;
    const db = this.getCustomerFirestore();
    if (!db) return false;

    try {
      const snap = await db.collection('shortlist')
        .where('applicant_id', '==', applicantId)
        .where('projectId', '==', projectId)
        .limit(1)
        .get();
      return !snap.empty;
    } catch {
      return false;
    }
  }

  async addCandidateToProject(candidate: Partial<ShortlistCandidate>, projectId: string): Promise<string | null> {
    const db = this.getCustomerFirestore();
    if (!db) throw new Error('Customer database not connected');

    const applicantId = candidate.applicant_id || '';
    if (applicantId) {
      if (this.isAlreadyShortlistedInProject(applicantId, projectId)) {
        console.log('[Shortlist] Candidate already in this project (memory):', applicantId);
        return null;
      }
      const existsInDb = await this.isAlreadyShortlistedInProjectDb(applicantId, projectId);
      if (existsInDb) {
        console.log('[Shortlist] Candidate already in this project (db):', applicantId);
        return null;
      }
    }

    const project = this.projects$.value.find(p => p.id === projectId);
    const projectName = project?.name || '';

    const userName = this.authService.returnUser() || '';
    const data: any = {
      full_name: candidate.full_name || '',
      linkedinUrl: candidate.linkedinUrl || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      statusId: candidate.statusId ?? 0,
      shortlistedBy: userName.split('@')[0] || 'Unknown',
      shortlistedByAvatar: candidate.shortlistedByAvatar || '',
      shortlistedOn: firebase.firestore.FieldValue.serverTimestamp(),
      job_title: candidate.job_title || '',
      job_company_name: candidate.job_company_name || '',
      job_company_logo: candidate.job_company_logo || '',
      isFavorite: candidate.isFavorite || false,
      jobOfferId: candidate.jobOfferId || '',
      jobOfferTitle: candidate.jobOfferTitle || '',
      projectId: projectId,
      projectName: projectName,
      score: candidate.score ?? null,
      reason: candidate.reason ?? null,
      headline: candidate.headline ?? '',
      location: candidate.location ?? '',
      profilePictureUrl: candidate.profilePictureUrl ?? '',
      tags: candidate.tags ?? [],
      applicant_id: applicantId
    };

    const docRef = await db.collection('shortlist').add(data);

    await db.collection('shortlist_projects').doc(projectId).update({
      candidateCount: firebase.firestore.FieldValue.increment(1)
    });

    const current = this.projects$.value;
    const updated = current.map(p =>
      p.id === projectId ? { ...p, candidateCount: p.candidateCount + 1 } : p
    );
    this.projects$.next(updated);

    const newCandidate: ShortlistCandidate = {
      ...data,
      id: docRef.id,
      shortlistedOn: new Date()
    };
    const currentCandidates = this.candidates$.value;
    this.candidates$.next([newCandidate, ...currentCandidates]);

    return docRef.id;
  }

  async deleteProject(projectId: string): Promise<void> {
    const db = this.getCustomerFirestore();
    if (!db) return;

    const candidatesSnap = await db
      .collection('shortlist')
      .where('projectId', '==', projectId)
      .get();

    const batch = db.batch();
    candidatesSnap.forEach(doc => {
      batch.delete(doc.ref);
    });
    batch.delete(db.collection('shortlist_projects').doc(projectId));
    await batch.commit();

    const currentCandidates = this.candidates$.value;
    this.candidates$.next(currentCandidates.filter(c => c.projectId !== projectId));

    const currentProjects = this.projects$.value;
    this.projects$.next(currentProjects.filter(p => p.id !== projectId));
  }

  getDistinctProjects(): { id: string; name: string }[] {
    return this.projects$.value.map(p => ({ id: p.id, name: p.name }));
  }

  async loadExternalJobOffers(): Promise<ExternalJobForMatching[]> {
    try {
      const externalJobsRef = this.customerDB.getExternalJobOffers();
      if (!externalJobsRef) return [];

      // Ordering in the query would silently hide every document without a
      // "created" field, which is how imported offers went missing. Sorting
      // happens client side so no offer can disappear.
      const snap = await externalJobsRef.get();
      if (!snap || snap.empty) return [];

      const docs = snap.docs.slice().sort((a: any, b: any) => this.jobSortValue(b.data()) - this.jobSortValue(a.data()));

      return docs.map((doc: any) => {
        const data = doc.data();
        return {
          id: doc.id,
          jobTitle: data.jobTitle || data.title || '',
          title: data.title || data.jobTitle || '',
          description: data.description || '',
          location: data.location || '',
          department: data.department || '',
          requirements: data.requirements || [],
          qualifications: data.qualifications || '',
          tasks: data.tasks || '',
          employmentType: data.employmentType || data.type || '',
          type: data.type || data.employmentType || '',
          applicationUrl: data.applicationUrl || '',
          sourceUrl: data.sourceUrl || '',
          company: data.company || ''
        };
      });
    } catch (error) {
      console.error('[Shortlist] loadExternalJobOffers error:', error);
      return [];
    }
  }

  private jobSortValue(data: any): number {
    const candidates = [data?.created, data?.createdISO, data?.importedAt, data?.updatedAt];
    for (const value of candidates) {
      if (!value) continue;
      if (typeof value?.toDate === 'function') return value.toDate().getTime();
      const parsed = new Date(value).getTime();
      if (!isNaN(parsed)) return parsed;
    }
    return 0;
  }

  /**
   * Imported applicants live in the applicants collection and are never written
   * to the shortlist, so the match modal could not see them at all. They are
   * loaded as an additional matching pool without being added to the shortlist.
   */
  async loadApplicantsForMatching(): Promise<ShortlistCandidate[]> {
    try {
      const applicantsRef = this.customerDB.getApplicants();
      if (!applicantsRef) return [];

      const snap = await applicantsRef.get();
      if (!snap || snap.empty) return [];

      return snap.docs.map((doc: any) => {
        const data = doc.data() || {};
        const first = data.firstName || data.vorname || '';
        const last = data.lastName || data.nachname || '';
        const name = [first, last].filter(Boolean).join(' ') || data.name || '';
        return {
          id: 'applicant:' + doc.id,
          applicant_id: doc.id,
          full_name: name,
          email: data.email || data.mail || '',
          phone: data.phoneNumber || data.telefon || data.phone || '',
          statusId: 0,
          shortlistedBy: '',
          shortlistedOn: new Date(0),
          job_title: data.position || data.jobTitle || data.job_title || data.berufsbezeichnung || '',
          headline: data.position || data.jobTitle || '',
          location: data.standort || data.location || data.ort || '',
          skills: this.asText(data.skills || data.kenntnisse || data.faehigkeiten),
          experience: this.asText(data.erfahrung || data.berufserfahrung || data.experience),
          education: this.asText(data.ausbildung || data.education),
          languages: this.asText(data.sprachen || data.languages),
          availability: this.asText(data.verfuegbarkeit || data.availability),
          notes: this.asText(data.notizen || data.interviewNotes || data.extractedText),
          isImportedApplicant: true
        } as ShortlistCandidate;
      }).filter((c: ShortlistCandidate) => !!(c.full_name || c.email));
    } catch (error) {
      console.error('[Shortlist] loadApplicantsForMatching error:', error);
      return [];
    }
  }

  private asText(value: any): string {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) return value.map(v => this.asText(v)).filter(Boolean).join('; ');
    if (typeof value === 'object') {
      return Object.keys(value).map(k => this.asText(value[k])).filter(Boolean).join('; ');
    }
    return String(value).trim();
  }

  matchCandidatesToJob(candidates: ShortlistCandidate[], job: ExternalJobForMatching): JobMatchResult[] {
    const results: JobMatchResult[] = [];

    for (const candidate of candidates) {
      const { score, reasons } = this.calculateMatchScore(candidate, job);
      if (score > 0) {
        results.push({
          candidate,
          job,
          matchScore: score,
          matchReasons: reasons
        });
      }
    }

    return results.sort((a, b) => b.matchScore - a.matchScore);
  }

  matchCandidatesToJobs(candidates: ShortlistCandidate[], jobs: ExternalJobForMatching[]): JobMatchResult[] {
    const results: JobMatchResult[] = [];

    for (const candidate of candidates) {
      for (const job of jobs) {
        const { score, reasons } = this.calculateMatchScore(candidate, job);
        if (score > 0) {
          results.push({
            candidate,
            job,
            matchScore: score,
            matchReasons: reasons
          });
        }
      }
    }

    return results.sort((a, b) => b.matchScore - a.matchScore);
  }

  private calculateMatchScore(candidate: ShortlistCandidate, job: ExternalJobForMatching): { score: number; reasons: string[] } {
    let score = 0;
    const reasons: string[] = [];

    const candidateTitle = (candidate.job_title || candidate.headline || '').toLowerCase().trim();
    const jobTitle = (job.jobTitle || job.title || '').toLowerCase().trim();

    // A candidate without a job title is still matchable through skills and
    // experience. Requiring the title dropped every imported applicant.
    const candidateProfile = [
      candidate.skills, candidate.experience, candidate.education,
      candidate.languages, candidate.notes
    ].filter(Boolean).join(' ').toLowerCase().trim();

    if (!jobTitle || (!candidateTitle && !candidateProfile)) {
      return { score: 0, reasons: [] };
    }

    if (!candidateTitle) {
      return this.scoreByProfile(candidate, job, candidateProfile, jobTitle);
    }

    const candidateTitleWords = this.extractKeywords(candidateTitle);
    const jobTitleWords = this.extractKeywords(jobTitle);
    const titleMatches = candidateTitleWords.filter(w => jobTitleWords.includes(w));

    if (titleMatches.length > 0) {
      const titleMatchRatio = titleMatches.length / Math.max(candidateTitleWords.length, jobTitleWords.length);
      score += Math.round(titleMatchRatio * 50);
      reasons.push(`Titel: ${titleMatches.join(', ')}`);
    }

    if (candidateTitle === jobTitle) {
      score += 30;
      reasons.push('Exakter Match');
    }

    const seniorityMatch = this.checkSeniorityMatch(candidateTitle, jobTitle);
    if (seniorityMatch) {
      score += 15;
      reasons.push(`Level: ${seniorityMatch}`);
    }

    const industryKeywords = this.extractIndustryKeywords(candidateTitle);
    const jobIndustryKeywords = this.extractIndustryKeywords(jobTitle);
    const industryMatches = industryKeywords.filter(k => jobIndustryKeywords.includes(k));
    if (industryMatches.length > 0) {
      score += industryMatches.length * 10;
      reasons.push(`Bereich: ${industryMatches.join(', ')}`);
    }

    const qualifications = (job.qualifications || '').toLowerCase();
    const tasks = (job.tasks || '').toLowerCase();
    const combinedJobText = `${jobTitle} ${qualifications} ${tasks}`;
    const candidateKeywords = this.extractIndustryKeywords(candidateTitle);
    const additionalMatches = candidateKeywords.filter(k => combinedJobText.includes(k) && !industryMatches.includes(k));
    if (additionalMatches.length > 0) {
      score += additionalMatches.length * 5;
    }

    const skillHits = this.matchSkills(candidate, job);
    if (skillHits.length > 0) {
      score += Math.min(skillHits.length * 8, 30);
      reasons.push(`Skills: ${skillHits.slice(0, 5).join(', ')}`);
    }

    const languageHits = this.matchLanguages(candidate, job);
    if (languageHits.length > 0) {
      score += 5;
      reasons.push(`Sprachen: ${languageHits.slice(0, 3).join(', ')}`);
    }

    if (this.matchLocation(candidate, job)) {
      score += 10;
      reasons.push(`Standort: ${candidate.location}`);
    }

    return { score: Math.min(score, 100), reasons };
  }

  /**
   * Scoring path for candidates that carry skills and experience but no job
   * title, which is the normal shape of an imported applicant.
   */
  private scoreByProfile(
    candidate: ShortlistCandidate,
    job: ExternalJobForMatching,
    candidateProfile: string,
    jobTitle: string
  ): { score: number; reasons: string[] } {
    let score = 0;
    const reasons: string[] = [];

    const skillHits = this.matchSkills(candidate, job);
    if (skillHits.length > 0) {
      score += Math.min(skillHits.length * 12, 60);
      reasons.push(`Skills: ${skillHits.slice(0, 5).join(', ')}`);
    }

    const jobTitleWords = this.extractKeywords(jobTitle);
    const titleInProfile = jobTitleWords.filter(w => candidateProfile.includes(w));
    if (titleInProfile.length > 0) {
      score += Math.min(titleInProfile.length * 10, 25);
      reasons.push(`Profil: ${titleInProfile.slice(0, 4).join(', ')}`);
    }

    const languageHits = this.matchLanguages(candidate, job);
    if (languageHits.length > 0) {
      score += 5;
      reasons.push(`Sprachen: ${languageHits.slice(0, 3).join(', ')}`);
    }

    if (this.matchLocation(candidate, job)) {
      score += 10;
      reasons.push(`Standort: ${candidate.location}`);
    }

    return { score: Math.min(score, 100), reasons };
  }

  private jobText(job: ExternalJobForMatching): string {
    return [
      job.jobTitle, job.title, job.description, job.qualifications,
      job.tasks, job.department, Array.isArray(job.requirements) ? job.requirements.join(' ') : job.requirements
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private matchSkills(candidate: ShortlistCandidate, job: ExternalJobForMatching): string[] {
    const raw = [candidate.skills, candidate.experience, candidate.education].filter(Boolean).join(';');
    if (!raw) return [];
    const jobText = this.jobText(job);
    if (!jobText) return [];

    const tokens = raw
      .split(/[;,|\/\n]+/)
      .map(t => t.trim())
      .filter(t => t.length > 2);

    const hits: string[] = [];
    const seen = new Set<string>();
    for (const token of tokens) {
      const normalized = token.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      if (jobText.includes(normalized)) {
        hits.push(token);
      }
    }
    return hits;
  }

  private matchLanguages(candidate: ShortlistCandidate, job: ExternalJobForMatching): string[] {
    if (!candidate.languages) return [];
    const jobText = this.jobText(job);
    if (!jobText) return [];
    const known = ['deutsch', 'english', 'englisch', 'französisch', 'french', 'spanisch', 'spanish',
                   'italienisch', 'italian', 'polnisch', 'polish', 'türkisch', 'turkish', 'russisch', 'russian'];
    const candidateLanguages = candidate.languages.toLowerCase();
    return known.filter(l => candidateLanguages.includes(l) && jobText.includes(l));
  }

  private matchLocation(candidate: ShortlistCandidate, job: ExternalJobForMatching): boolean {
    const candidateLocation = (candidate.location || '').toLowerCase().trim();
    const jobLocation = (job.location || '').toLowerCase().trim();
    if (!candidateLocation || !jobLocation) return false;
    const city = candidateLocation.split(',')[0].trim();
    if (city.length < 3) return false;
    return jobLocation.includes(city) || candidateLocation.includes(jobLocation.split(',')[0].trim());
  }

  private extractKeywords(text: string): string[] {
    const stopWords = ['der', 'die', 'das', 'und', 'oder', 'für', 'bei', 'in', 'mit', 'von', 'zu', 'am', 
                       'the', 'and', 'or', 'for', 'at', 'in', 'with', 'of', 'to', 'm/w/d', 'm/f/d', 'f/m/d'];
    return text
      .toLowerCase()
      .replace(/[^\w\säöüß]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.includes(w));
  }

  private checkSeniorityMatch(candidateTitle: string, jobTitle: string): string | null {
    const seniorityLevels = [
      { keywords: ['senior', 'sr.', 'sr ', 'lead', 'principal'], level: 'senior' },
      { keywords: ['junior', 'jr.', 'jr ', 'entry'], level: 'junior' },
      { keywords: ['mid', 'regular', 'intermediate'], level: 'mid' },
      { keywords: ['head', 'director', 'vp', 'chief', 'leiter', 'leitung'], level: 'executive' },
      { keywords: ['manager', 'teamlead', 'team lead'], level: 'manager' }
    ];

    for (const { keywords, level } of seniorityLevels) {
      const candidateHas = keywords.some(k => candidateTitle.includes(k));
      const jobHas = keywords.some(k => jobTitle.includes(k));
      if (candidateHas && jobHas) {
        return level;
      }
    }
    return null;
  }

  private extractIndustryKeywords(title: string): string[] {
    const industryTerms = [
      'software', 'developer', 'entwickler', 'engineer', 'ingenieur',
      'sales', 'vertrieb', 'marketing', 'hr', 'personal', 'recruiting',
      'finance', 'finanzen', 'accounting', 'buchhaltung',
      'design', 'ux', 'ui', 'product', 'produkt',
      'data', 'daten', 'analyst', 'science', 'wissenschaft',
      'devops', 'cloud', 'security', 'sicherheit',
      'consulting', 'beratung', 'project', 'projekt',
      'support', 'service', 'customer', 'kunden',
      'frontend', 'backend', 'fullstack', 'full-stack',
      'mobile', 'web', 'java', 'python', 'javascript', 'react', 'angular',
      'pflege', 'arzt', 'medizin', 'health', 'gesundheit',
      'logistik', 'logistics', 'warehouse', 'lager',
      'produktion', 'production', 'manufacturing', 'fertigung'
    ];
    
    const lowerTitle = title.toLowerCase();
    return industryTerms.filter(term => lowerTitle.includes(term));
  }

  async isHybridCustomer(): Promise<boolean> {
    const currentPlan = this.customerDB.returnCurrentPlan() || '';
    return currentPlan.toLowerCase().includes('hybrid');
  }
}
